import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Button,
    Container,
    Menu,
    MenuItem,
    MenuItems,
    MenuSeparator,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    TabNav,
    TabNavItem,
} from '@/platform/ui';
import { MenuButton } from '@headlessui/react';
import {
    EllipsisVerticalIcon,
    PencilIcon,
    PlusIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { Link, router, usePage } from '@inertiajs/react';

const IndexView = () => {
    const { auth, office_contacts, organisations } = usePage().props;
    const { filters, updateFilters, isSorted } = useFilters([
        'office_contacts',
    ]);
    const confirm = useConfirmationStore(state => state.confirm);

    const destroy = officeContact => {
        router.delete(`/office-contacts/${officeContact.id}`, {
            preserveScroll: true,
            preserveState: true,
        });
    };

    const confirmDelete = officeContact =>
        confirm({
            title: 'Contactpersoon verwijderen?',
            description: `Weet je zeker dat je ${officeContact.name} wilt verwijderen?`,
            confirmLabel: 'Verwijderen',
            onConfirm: close => {
                destroy(officeContact);
                close();
            },
        });

    const canUpdate = auth?.permissions?.includes('update office contacts');
    const canDelete = auth?.permissions?.includes('delete office contacts');

    return (
        <Container>
            <TabNav label='Organisatie'>
                {(organisations || []).map(organisation => (
                    <TabNavItem
                        key={organisation}
                        active={filters?.organisation === organisation}
                        onClick={() =>
                            updateFilters('organisation', organisation)
                        }
                    >
                        {organisation}
                    </TabNavItem>
                ))}
            </TabNav>

            <Segment>
                <SegmentHeader
                    header={`Contactpersonen (${office_contacts?.meta?.total || 0})`}
                >
                    {auth?.permissions?.includes('create office contacts') && (
                        <Button
                            variant='primary'
                            as={Link}
                            href='/office-contacts/create'
                        >
                            <PlusIcon className='size-5' />
                            Nieuwe contactpersoon
                        </Button>
                    )}
                </SegmentHeader>
                <SegmentContent>
                    <SearchBarFilter only={['office_contacts']} />

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell
                                    sorted={isSorted('organisation')}
                                    onClick={() =>
                                        updateFilters('sort', 'organisation')
                                    }
                                >
                                    Organisatie
                                </TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted('office')}
                                    onClick={() =>
                                        updateFilters('sort', 'office')
                                    }
                                >
                                    Kantoor
                                </TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted('gender')}
                                    onClick={() =>
                                        updateFilters('sort', 'gender')
                                    }
                                >
                                    Geslacht
                                </TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted('last_name')}
                                    onClick={() =>
                                        updateFilters('sort', 'last_name')
                                    }
                                >
                                    Naam
                                </TableHeaderCell>
                                <TableHeaderCell>Tel.nr</TableHeaderCell>
                                <TableHeaderCell>Mobiel nr.</TableHeaderCell>
                                <TableHeaderCell>E-mailadres</TableHeaderCell>
                                <TableHeaderCell>Postcode</TableHeaderCell>
                                <TableHeaderCell>Postbus</TableHeaderCell>
                                <TableHeaderCell>Plaats</TableHeaderCell>
                                <TableHeaderCell
                                    className='w-px'
                                    textAlign='right'
                                />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {office_contacts?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={11} />
                            )}

                            {office_contacts?.data?.map(officeContact => (
                                <TableRow key={officeContact.id}>
                                    <TableCell>
                                        {officeContact.organisation}
                                    </TableCell>
                                    <TableCell>
                                        {officeContact.office}
                                    </TableCell>
                                    <TableCell>
                                        {officeContact.gender}
                                    </TableCell>
                                    <TableCell>
                                        {canUpdate ? (
                                            <Link
                                                href={`/office-contacts/${officeContact.id}/edit`}
                                            >
                                                {officeContact.name}
                                            </Link>
                                        ) : (
                                            officeContact.name
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {officeContact.telephone_number}
                                    </TableCell>
                                    <TableCell>
                                        {officeContact.mobile_number}
                                    </TableCell>
                                    <TableCell>{officeContact.email}</TableCell>
                                    <TableCell>
                                        {officeContact.zipcode}
                                    </TableCell>
                                    <TableCell>
                                        {officeContact.postbus}
                                    </TableCell>
                                    <TableCell>{officeContact.city}</TableCell>
                                    <TableCell
                                        className='w-px'
                                        singleLine
                                        textAlign='right'
                                    >
                                        {(canUpdate || canDelete) && (
                                            <Menu>
                                                <MenuButton
                                                    aria-label={`Acties voor ${officeContact.name}`}
                                                >
                                                    <EllipsisVerticalIcon className='size-5' />
                                                </MenuButton>
                                                <MenuItems anchor='bottom end'>
                                                    {canUpdate && (
                                                        <MenuItem
                                                            as={Link}
                                                            href={`/office-contacts/${officeContact.id}/edit`}
                                                            icon={PencilIcon}
                                                        >
                                                            Wijzigen
                                                        </MenuItem>
                                                    )}
                                                    {canUpdate && canDelete && (
                                                        <MenuSeparator />
                                                    )}
                                                    {canDelete && (
                                                        <MenuItem
                                                            className='text-red-600'
                                                            onClick={() =>
                                                                confirmDelete(
                                                                    officeContact
                                                                )
                                                            }
                                                            icon={TrashIcon}
                                                        >
                                                            Verwijderen
                                                        </MenuItem>
                                                    )}
                                                </MenuItems>
                                            </Menu>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>
                <SegmentFooter>
                    <CustomPagination
                        meta={office_contacts?.meta}
                        only={['office_contacts']}
                    />
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default IndexView;
